import { formatDate, formatDateTime } from "@/lib/utils";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Filter,
  Eye,
  Play,
  FileText,
  X,
  MoreVertical,
} from "lucide-react";
import { toast } from "sonner";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { DataTable } from "@/components/ui/data-table";
import { ReactSelect } from "@/components/ui/react-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PageContainer } from "@/components/layout/PageContainer";
import RecordNotFound from "@/core/components/RecordNotFound";

const CartHistory = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCart, setSelectedCart] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);


  const permissions = useSelector((state) => state.userpermission_data);
  const hasPermission = React.useCallback(
    (permission) => {
      const perms = permissions || [];
      return perms && perms.includes(permission);
    },
    [permissions],
  );

  const store_id = useSelector((state) => state.current_store?.id || null);
  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  const [filters, setFilters] = useState({
    customer_id: "",
    date_from: "",
    date_to: "",
    item_count_min: "",
    item_count_max: "",
    subtotal_min: "",
    subtotal_max: "",
    total_min: "",
    total_max: "",
    status: "",
    store_id: store_id || "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    customer_id: "",
    date_from: "",
    date_to: "",
    item_count_min: "",
    item_count_max: "",
    subtotal_min: "",
    subtotal_max: "",
    total_min: "",
    total_max: "",
    status: "",
    store_id: store_id || "",
  });

  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerLabels, setCustomerLabels] = useState({});

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCustomerSearchTerm(customerSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  const { data: customers = [], isFetching: isCustomersFetching } = useQuery({
    queryKey: ["customers", customerSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.SALES.CUSTOMERS, {
        params: { search: customerSearchTerm || undefined },
      });
      return res.data.data || [];
    },
  });

  React.useEffect(() => {
    if (customers.length > 0) {
      setCustomerLabels((prev) => {
        const next = { ...prev };
        customers.forEach((c) => {
          let displayName = c.full_name || c.name;
          if (c.customer_type !== "individual" && c.company) {
            displayName = c.company;
          }
          const labels = [displayName, c.phone].filter(Boolean);
          next[String(c.id)] = labels.join(" - ");
        });
        return next;
      });
    }
  }, [customers]);

  const { data: cartData, isLoading } = useQuery({
    queryKey: ["carts", currentPage, pageSize, appliedFilters, store_id],
    queryFn: async () => {
      const params = {
        with: ["customer", "store", "user", "items", "items.product"],
        store_id: store_id,
        page: currentPage,
        per_page: pageSize,
        ...appliedFilters,
      };

      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === "all"
        ) {
          delete params[key];
        }
      });

      const response = await apiClient.get(API_ENDPOINTS.SALES.POS_CART, {
        params,
      });
      return response.data;
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to fetch cart history",
      );
    },
  });

  React.useEffect(() => {
    if (cartData?.data) {
      const cLabels = {};
      cartData.data.forEach((cart) => {
        if (cart.customer) {
          const c = cart.customer;
          let displayName = c.full_name || c.name;
          if (c.customer_type !== "individual" && c.company) {
            displayName = c.company;
          }
          const labels = [displayName, c.phone].filter(Boolean);
          cLabels[String(c.id)] = labels.join(" - ");
        }
      });
      setCustomerLabels((prev) => ({ ...prev, ...cLabels }));
    }
  }, [cartData]);

  const carts = React.useMemo(() => cartData?.data || [], [cartData?.data]);
  const paginationMeta = {
    current_page: cartData?.meta?.current_page || cartData?.current_page || 1,
    last_page: cartData?.meta?.last_page || cartData?.last_page || 1,
    per_page: cartData?.meta?.per_page || cartData?.per_page || 15,
    total: cartData?.meta?.total || cartData?.total || carts.length,
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      customer_id: "",
      date_from: "",
      date_to: "",
      item_count_min: "",
      item_count_max: "",
      subtotal_min: "",
      subtotal_max: "",
      total_min: "",
      total_max: "",
      status: "",
      store_id: store_id || "",
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setCurrentPage(1);
  };

  const handleViewDetails = (cart) => {
    setSelectedCart(cart);
    setShowDetailsModal(true);
  };

  const handleContinueSelling = (cartId) => {
    navigate(`/pos?cartId=${cartId}`);
  };

  const filteredData = useMemo(() => {
    return carts.filter((entry) =>
      Object.keys(entry).some((key) => {
        const value = entry[key];
        if (typeof value === "string") {
          return value.toLowerCase().includes(searchText.toLowerCase());
        }
        if (key === "customer" && value) {
          const displayName =
            value.customer_type !== "individual" && value.company
              ? value.company
              : value.full_name || "-";
          return displayName.toLowerCase().includes(searchText.toLowerCase());
        }
        return false;
      }),
    );
  }, [carts, searchText]);

  const getStatusBadgeVariant = (status) => {
    const statusMap = {
      active: "info",
      completed: "success",
      cancelled: "destructive",
      "on-hold": "warning",
      "payment-pending": "warning",
    };
    return statusMap[status] || "secondary";
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "cart_number",
        header: "Cart Number",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.cart_number}</span>
        ),
      },
      {
        accessorKey: "customer",
        header: "Customer",
        cell: ({ row }) => {
          const customer = row.original.customer;
          if (!customer)
            return <span className="text-muted-foreground">-</span>;

          if (!hasPermission("cart read")) {
            return <RecordNotFound />;
          }
          return (
            <span className="text-sm">
              {customer.customer_type !== "individual" && customer.company
                ? customer.company
                : customer.full_name || "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "item_count",
        header: "Items",
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal">
            {row.original.item_count}
          </Badge>
        ),
      },
      {
        accessorKey: "subtotal",
        header: "Subtotal",
        cell: ({ row }) => (
          <span className="font-medium">
            {currencySymbol}
            {parseFloat(row.original.subtotal || 0).toFixed(2)}
          </span>
        ),
      },
      {
        accessorKey: "total",
        header: "Total",
        cell: ({ row }) => (
          <span className="font-semibold text-green-600">
            {currencySymbol}
            {parseFloat(row.original.total || 0).toFixed(2)}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            variant={getStatusBadgeVariant(row.original.status)}
            className="capitalize"
          >
            {row.original.status.replace(/-/g, " ")}
          </Badge>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        cell: ({ row }) => (
          <span className="text-sm">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleViewDetails(row.original)}>
                <Eye className="mr-2 h-4 w-4" /> View Details
              </DropdownMenuItem>
              {(row.original.status === "active" ||
                row.original.status === "on-hold") && (
                  <DropdownMenuItem
                    onClick={() => handleContinueSelling(row.original.id)}
                  >
                    <Play className="mr-2 h-4 w-4 text-green-600" /> Continue
                    Selling
                  </DropdownMenuItem>
                )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencySymbol],
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Cart History"
          subtitle="View All Cart Records"
        />
        <PageHeaderActions>
          <Button variant="outline" onClick={() => setShowFilterModal(true)}>
            <Filter className="mr-2 h-4 w-4" />
            Filters
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search carts..."
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={filteredData}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          pageCount={paginationMeta.last_page}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            }
          }}
          isLoading={isLoading}
          enableRowSelection={true}
          onRowClick={(row) => handleViewDetails(row)}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total Carts: {paginationMeta.total || 0}
              </span>
            </div>
          }
        />
      </TableCard>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filter Carts
            </DialogTitle>
            <DialogDescription>
              Filter carts by customer, status, date, and more.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Customer</Label>
              <ReactSelect
                onInputChange={(value) => setCustomerSearchInput(value)}
                options={[{ value: "all", label: "All Customers" }].concat(
                  customers.map((c) => {
                    let displayName = c.full_name || c.name;
                    if (c.customer_type !== "individual" && c.company) {
                      displayName = c.company;
                    }
                    const labels = [displayName, c.phone].filter(Boolean);
                    return {
                      value: String(c.id),
                      label: labels.join(" - "),
                    };
                  }),
                )}
                value={
                  filters.customer_id && filters.customer_id !== "all"
                    ? {
                      value: filters.customer_id,
                      label:
                        customerLabels[String(filters.customer_id)] ||
                        customers.find(
                          (c) => String(c.id) === String(filters.customer_id),
                        )?.full_name ||
                        "Select Customer",
                    }
                    : { value: "all", label: "All Customers" }
                }
                onChange={(opt) =>
                  setFilters({
                    ...filters,
                    customer_id: opt ? opt.value : "all",
                  })
                }
                placeholder="Search Customer..."
                isLoading={isCustomersFetching}
                isClearable={false}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) =>
                  setFilters({ ...filters, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="on-hold">On Hold</SelectItem>
                  <SelectItem value="payment-pending">
                    Payment Pending
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Date From</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) =>
                  setFilters({ ...filters, date_from: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Date To</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) =>
                  setFilters({ ...filters, date_to: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Min Items</Label>
              <Input
                type="number"
                min="0"
                value={filters.item_count_min}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    item_count_min: e.target.value,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Max Items</Label>
              <Input
                type="number"
                min="0"
                value={filters.item_count_max}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    item_count_max: e.target.value,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Min Subtotal</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={filters.subtotal_min}
                onChange={(e) =>
                  setFilters({ ...filters, subtotal_min: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Max Subtotal</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={filters.subtotal_max}
                onChange={(e) =>
                  setFilters({ ...filters, subtotal_max: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Min Total</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={filters.total_min}
                onChange={(e) =>
                  setFilters({ ...filters, total_min: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Max Total</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={filters.total_max}
                onChange={(e) =>
                  setFilters({ ...filters, total_max: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={handleResetFilters}>
              <X className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button onClick={handleApplyFilters}>
              <Filter className="mr-2 h-4 w-4" />
              Apply Filters
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Cart Details - {selectedCart?.cart_number}
            </DialogTitle>
          </DialogHeader>

          {selectedCart && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Cart Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Cart Number:
                      </span>
                      <span className="font-medium">
                        {selectedCart.cart_number}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge
                        variant={getStatusBadgeVariant(selectedCart.status)}
                        className="capitalize"
                      >
                        {selectedCart.status.replace(/-/g, " ")}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Customer:</span>
                      <span className="font-medium">
                        {selectedCart.customer
                          ? selectedCart.customer.customer_type !==
                            "individual" && selectedCart.customer.company
                            ? selectedCart.customer.company
                            : selectedCart.customer.full_name || "-"
                          : "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Created At:</span>
                      <span className="font-medium">
                        {formatDateTime(selectedCart.created_at)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Financial Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal:</span>
                      <span className="font-medium">
                        {currencySymbol}
                        {parseFloat(selectedCart.subtotal || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax:</span>
                      <span className="font-medium">
                        {currencySymbol}
                        {parseFloat(selectedCart.tax_amount || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Discount:</span>
                      <span className="font-medium text-destructive">
                        -{currencySymbol}
                        {parseFloat(selectedCart.discount_amount || 0).toFixed(
                          2,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-muted-foreground font-semibold">
                        Total:
                      </span>
                      <span className="font-bold text-lg text-green-600">
                        {currencySymbol}
                        {parseFloat(selectedCart.total || 0).toFixed(2)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {selectedCart.items && selectedCart.items.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Cart Items</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border overflow-x-auto">
                      <ShadcnTable>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">
                              Quantity
                            </TableHead>
                            <TableHead className="text-right">
                              Unit Price
                            </TableHead>
                            <TableHead className="text-right">
                              Discount
                            </TableHead>
                            <TableHead className="text-right">Tax</TableHead>
                            <TableHead className="text-right">
                              Line Total
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedCart.items.map((item, index) => {
                            let productName = "-";
                            if (item.product?.name) {
                              productName = item.product.name;
                            } else if (item.product_data) {
                              try {
                                const productData =
                                  typeof item.product_data === "string"
                                    ? JSON.parse(item.product_data)
                                    : item.product_data;
                                productName =
                                  productData.name || item.product_id;
                              } catch {
                                productName = item.product_id;
                              }
                            } else {
                              productName = item.product_id;
                            }

                            return (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium">
                                  {index + 1}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                      {productName}
                                    </div>
                                    {(() => {
                                      let serials = [];
                                      if (Array.isArray(item.serial_numbers)) {
                                        serials = item.serial_numbers;
                                      } else if (
                                        typeof item.serial_numbers ===
                                        "string" &&
                                        item.serial_numbers !== "null" &&
                                        item.serial_numbers.trim() !== ""
                                      ) {
                                        try {
                                          serials = JSON.parse(
                                            item.serial_numbers,
                                          );
                                        } catch {
                                          serials = [item.serial_numbers];
                                        }
                                      } else if (item.serial_number) {
                                        serials = [item.serial_number];
                                      }
                                      if (serials.length > 0) {
                                        return (
                                          <span className="text-xs text-muted-foreground mt-0.5">
                                            Serial: {serials.join(", ")}
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                    {item.notes && (
                                      <span
                                        className="text-xs text-muted-foreground mt-0.5 line-clamp-2"
                                        title={item.notes}
                                      >
                                        {item.notes}
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  {item.quantity}
                                </TableCell>
                                <TableCell className="text-right">
                                  {currencySymbol}
                                  {parseFloat(item.unit_price || 0).toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right text-destructive">
                                  {currencySymbol}
                                  {parseFloat(
                                    item.discount_amount || 0,
                                  ).toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {currencySymbol}
                                  {parseFloat(item.tax_amount || 0).toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right font-semibold">
                                  {currencySymbol}
                                  {parseFloat(item.line_total || 0).toFixed(2)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </ShadcnTable>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default CartHistory;
